import React from 'react'
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Slider,
  Button,
  TextField,
  Chip,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Clear as ClearIcon } from '@mui/icons-material'
import { tr } from 'date-fns/locale'

const TransactionFilters = ({ filters, onChange, onReset }) => {
  const handleFilterChange = (field, value) => {
    onChange({
      ...filters,
      [field]: value,
    })
  }

  const handleAmountRangeChange = (event, newValue) => {
    handleFilterChange('amountRange', newValue)
  }

  const handleDateRangeChange = (field, value) => {
    handleFilterChange('dateRange', {
      ...filters.dateRange,
      [field]: value,
    })
  }

  const amountMarks = [
    { value: 0, label: '0' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 500, label: '500' },
    { value: 1000, label: '1000+' },
  ]

  const getActiveFiltersCount = () => {
    let count = 0
    if (filters.type && filters.type !== 'all') count++
    if (filters.status && filters.status !== 'all') count++
    if (filters.dateRange?.startDate || filters.dateRange?.endDate) count++
    if (filters.amountRange && (filters.amountRange[0] !== 0 || filters.amountRange[1] !== 1000)) count++
    return count
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtreler
            </Typography>
            {getActiveFiltersCount() > 0 && (
              <Chip 
                label={`${getActiveFiltersCount()} aktif filtre`} 
                size="small" 
                color="primary"
              />
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={onReset}
            disabled={getActiveFiltersCount() === 0}
          >
            Filtreleri Temizle
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Transaction Type Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>İşlem Tipi</InputLabel>
              <Select
                value={filters.type || 'all'}
                label="İşlem Tipi"
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="purchase">Satın Alma</MenuItem>
                <MenuItem value="rental">Kiralama</MenuItem>
                <MenuItem value="admin_adjustment">Admin Düzeltme</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>İşlem Durumu</InputLabel>
              <Select
                value={filters.status || 'all'}
                label="İşlem Durumu"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="completed">Tamamlandı</MenuItem>
                <MenuItem value="pending">Beklemede</MenuItem>
                <MenuItem value="failed">Başarısız</MenuItem>
                <MenuItem value="cancelled">İptal Edildi</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range - Start */}
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Başlangıç Tarihi"
              value={filters.dateRange?.startDate || null}
              onChange={(value) => handleDateRangeChange('startDate', value)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              maxDate={new Date()}
            />
          </Grid>

          {/* Date Range - End */}
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Bitiş Tarihi"
              value={filters.dateRange?.endDate || null}
              onChange={(value) => handleDateRangeChange('endDate', value)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              maxDate={new Date()}
              minDate={filters.dateRange?.startDate || undefined}
            />
          </Grid>

          {/* Amount Range Filter */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Kredi Miktarı Aralığı
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={filters.amountRange || [0, 1000]}
                onChange={handleAmountRangeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value} kredi`}
                min={0}
                max={1000}
                step={10}
                marks={amountMarks}
                sx={{ mt: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {filters.amountRange?.[0] || 0} kredi
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filters.amountRange?.[1] || 1000} kredi
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Quick Filters */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Hızlı Filtreler
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                size="small"
                variant={filters.dateRange?.quickFilter === 'today' ? 'contained' : 'outlined'}
                onClick={() => {
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  const tomorrow = new Date(today)
                  tomorrow.setDate(tomorrow.getDate() + 1)
                  
                  handleFilterChange('dateRange', {
                    startDate: today,
                    endDate: tomorrow,
                    quickFilter: 'today'
                  })
                }}
              >
                Bugün
              </Button>
              
              <Button
                size="small"
                variant={filters.dateRange?.quickFilter === 'week' ? 'contained' : 'outlined'}
                onClick={() => {
                  const today = new Date()
                  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
                  
                  handleFilterChange('dateRange', {
                    startDate: weekAgo,
                    endDate: today,
                    quickFilter: 'week'
                  })
                }}
              >
                Son 7 Gün
              </Button>
              
              <Button
                size="small"
                variant={filters.dateRange?.quickFilter === 'month' ? 'contained' : 'outlined'}
                onClick={() => {
                  const today = new Date()
                  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
                  
                  handleFilterChange('dateRange', {
                    startDate: monthAgo,
                    endDate: today,
                    quickFilter: 'month'
                  })
                }}
              >
                Son 30 Gün
              </Button>
              
              <Button
                size="small"
                variant={filters.type === 'purchase' && filters.status === 'completed' ? 'contained' : 'outlined'}
                onClick={() => {
                  handleFilterChange('type', 'purchase')
                  handleFilterChange('status', 'completed')
                }}
              >
                Başarılı Satın Almalar
              </Button>
              
              <Button
                size="small"
                variant={filters.type === 'rental' ? 'contained' : 'outlined'}
                onClick={() => {
                  handleFilterChange('type', 'rental')
                  handleFilterChange('status', 'all')
                }}
              >
                Kiralamalar
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Applied Filters Summary */}
        {getActiveFiltersCount() > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Aktif Filtreler:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filters.type !== 'all' && (
                <Chip
                  label={`Tip: ${filters.type}`}
                  size="small"
                  onDelete={() => handleFilterChange('type', 'all')}
                  color="primary"
                />
              )}
              {filters.status !== 'all' && (
                <Chip
                  label={`Durum: ${filters.status}`}
                  size="small"
                  onDelete={() => handleFilterChange('status', 'all')}
                  color="secondary"
                />
              )}
              {(filters.dateRange?.startDate || filters.dateRange?.endDate) && (
                <Chip
                  label="Tarih aralığı"
                  size="small"
                  onDelete={() => handleFilterChange('dateRange', null)}
                  color="info"
                />
              )}
              {(filters.amountRange?.[0] !== 0 || filters.amountRange?.[1] !== 1000) && (
                <Chip
                  label={`Miktar: ${filters.amountRange?.[0]}-${filters.amountRange?.[1]}`}
                  size="small"
                  onDelete={() => handleFilterChange('amountRange', [0, 1000])}
                  color="success"
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default TransactionFilters